import type { DraftLine, Orders, PendingSubmission, Session, Setup } from "./types";
import { safeJsonParse, uuid } from "./utils";
import { UI_LANGUAGE_KEY, selectLanguageAccount, setLanguage } from "./i18n";

const PREFIX = "easyorders.pwa";
const SESSION_KEY = `${PREFIX}.session`;
const REMEMBER_KEY = `${PREFIX}.remember`;
const DEVICE_ID_KEY = `${PREFIX}.deviceId`;
const THEME_KEY = `${PREFIX}.theme`;
const TABLE_PAGE_KEY = `${PREFIX}.tablePageId`;

function scopedKey(session: Pick<Session, "businessSlug">, name: string): string {
  return `${PREFIX}.${session.businessSlug}.${name}`;
}

export function deviceId(): string {
  let value = localStorage.getItem(DEVICE_ID_KEY);
  if (!value) {
    value = uuid();
    localStorage.setItem(DEVICE_ID_KEY, value);
  }
  return value;
}

export function deviceName(): string {
  const platform = navigator.userAgent.includes("iPhone")
    ? "iPhone"
    : navigator.userAgent.includes("iPad")
      ? "iPad"
      : "Web";
  return `EasyOrders ${platform}`;
}

/**
 * "Να με θυμάσαι" keeps the session in localStorage so the app opens signed in. Without it the
 * session lives in sessionStorage and disappears when the app is closed, matching Android.
 */
export function loadSession(): Session | null {
  const remembered = safeJsonParse<Session | null>(localStorage.getItem(SESSION_KEY), null);
  if (remembered) {
    selectLanguageAccount(remembered);
    return remembered;
  }
  const session = safeJsonParse<Session | null>(sessionStorage.getItem(SESSION_KEY), null);
  selectLanguageAccount(session);
  return session;
}

export function saveSession(session: Session | null, remember = true): void {
  selectLanguageAccount(session);
  if (!session) {
    localStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_KEY);
    return;
  }
  localStorage.setItem(REMEMBER_KEY, remember ? "1" : "0");
  const payload = JSON.stringify(session);
  if (remember) {
    localStorage.setItem(SESSION_KEY, payload);
    sessionStorage.removeItem(SESSION_KEY);
  } else {
    sessionStorage.setItem(SESSION_KEY, payload);
    localStorage.removeItem(SESSION_KEY);
  }
}

export function loadRememberPreference(): boolean {
  const stored = localStorage.getItem(REMEMBER_KEY);
  if (stored === "0") {
    return false;
  }
  // Dedicated restaurant devices should reopen signed in unless the user opts out.
  return true;
}

export function loadSetup(session: Session): Setup | null {
  return safeJsonParse<Setup | null>(localStorage.getItem(scopedKey(session, "setup")), null);
}

export function saveSetup(session: Session, setup: Setup): void {
  setup = { ...setup, users: setup.users.map(({ password: _password, create_user: _create, ...user }) => user) };
  const key = scopedKey(session, "setup");
  try {
    localStorage.setItem(key, JSON.stringify(setup));
  } catch {
    // Product photos can exhaust Safari's small localStorage quota. Keep the operational
    // setup available offline and let images return on the next online setup refresh.
    const withoutImages: Setup = {
      ...setup,
      products: setup.products.map((product) => ({ ...product, image_data: "" }))
    };
    try {
      localStorage.setItem(key, JSON.stringify(withoutImages));
    } catch {
      // Live in-memory state remains usable even when this device has no cache space left.
    }
  }
}

export function loadOrders(session: Session): Orders | null {
  return safeJsonParse<Orders | null>(localStorage.getItem(scopedKey(session, "orders")), null);
}

export function saveOrders(session: Session, orders: Orders): void {
  localStorage.setItem(scopedKey(session, "orders"), JSON.stringify(orders));
}

export function loadDraft(session: Session, tableLabel: string): DraftLine[] {
  return safeJsonParse<DraftLine[]>(
    localStorage.getItem(scopedKey(session, `draft.${tableLabel}`)),
    []
  );
}

export function saveDraft(session: Session, tableLabel: string, lines: DraftLine[]): void {
  const key = scopedKey(session, `draft.${tableLabel}`);
  if (lines.length) {
    localStorage.setItem(key, JSON.stringify(lines));
  } else {
    localStorage.removeItem(key);
  }
}

export function loadTablePageId(): number | null {
  const value = Number.parseInt(localStorage.getItem(TABLE_PAGE_KEY) ?? "", 10);
  return Number.isFinite(value) && value > 0 ? value : null;
}

export function saveTablePageId(pageId: number): void {
  if (pageId > 0) {
    localStorage.setItem(TABLE_PAGE_KEY, String(pageId));
  }
}

export function loadPending(session: Session): PendingSubmission[] {
  return safeJsonParse<PendingSubmission[]>(
    localStorage.getItem(scopedKey(session, "pending")),
    []
  );
}

export function savePending(session: Session, pending: PendingSubmission[]): void {
  localStorage.setItem(scopedKey(session, "pending"), JSON.stringify(pending));
}

export type ThemePreference = "system" | "light" | "dark";

export function loadTheme(): ThemePreference {
  const stored = localStorage.getItem(THEME_KEY);
  return stored === "light" || stored === "dark" ? stored : "system";
}

export function saveTheme(theme: ThemePreference): void {
  localStorage.setItem(THEME_KEY, theme);
}

/* The tables screen unmounts when you switch tabs, so its view toggles have to live outside
   React state or they snap back to grid/admin every time — the app keeps the same two in
   SharedPreferences. */
const TABLE_VIEW_KEY = `${PREFIX}.tableViewMode`;
const TABLE_PREVIEW_KEY = `${PREFIX}.tableEmployeePreview`;
const TABLE_TOOLS_OPEN_KEY = `${PREFIX}.tableToolsOpen`;
const TABLE_SORT_KEY = `${PREFIX}.tableSortMode`;

export type TableSortMode = "numeric" | "open";

export function loadTableListView(): boolean {
  return localStorage.getItem(TABLE_VIEW_KEY) === "rows";
}

export function saveTableListView(listView: boolean): void {
  localStorage.setItem(TABLE_VIEW_KEY, listView ? "rows" : "grid");
}

export function loadTableEmployeePreview(): boolean {
  return localStorage.getItem(TABLE_PREVIEW_KEY) === "1";
}

export function saveTableEmployeePreview(preview: boolean): void {
  localStorage.setItem(TABLE_PREVIEW_KEY, preview ? "1" : "0");
}

export function loadTableToolsOpen(): boolean {
  return localStorage.getItem(TABLE_TOOLS_OPEN_KEY) === "1";
}

export function saveTableToolsOpen(open: boolean): void {
  localStorage.setItem(TABLE_TOOLS_OPEN_KEY, open ? "1" : "0");
}

export function loadTableSortMode(): TableSortMode {
  return localStorage.getItem(TABLE_SORT_KEY) === "open" ? "open" : "numeric";
}

export function saveTableSortMode(mode: TableSortMode): void {
  localStorage.setItem(TABLE_SORT_KEY, mode === "open" ? "open" : "numeric");
}

/* Κείμενο / UI preferences. Android keeps these in SharedPreferences; the web equivalent is
   localStorage so they survive a tab switch and a reload. */
const TEXT_SIZE_KEY = `${PREFIX}.textSizePercent`;
const TEXT_BOLD_KEY = `${PREFIX}.textBold`;
const FONT_FAMILY_KEY = `${PREFIX}.fontFamily`;
const ORDER_SOUND_KEY = `${PREFIX}.orderSound`;
const PRODUCT_SCROLL_KEY = `${PREFIX}.productScrollMode`;
const PRODUCT_DISPLAY_KEY = `${PREFIX}.productDisplayMode`;
const PRODUCT_IMAGES_KEY = `${PREFIX}.productImagesVisible`;
const HISTORY_EXPANSION_KEY = `${PREFIX}.historyExpansionMode`;
const DETAILS_PROMPT_KEY = `${PREFIX}.detailsPromptOnAdd`;
const KEEP_UNSENT_DRAFT_KEY = `${PREFIX}.keepUnsentDraft`;
const CLOSE_BUTTON_STYLE_KEY = `${PREFIX}.closeButtonStyle`;
const STICKY_PREFIX = `${PREFIX}.sticky.`;

export const TEXT_SIZE_PERCENT_OPTIONS = [100, 115, 130, 145];

export function loadTextSizePercent(): number {
  const stored = Number(localStorage.getItem(TEXT_SIZE_KEY));
  return TEXT_SIZE_PERCENT_OPTIONS.includes(stored) ? stored : 100;
}

export function saveTextSizePercent(percent: number): void {
  localStorage.setItem(TEXT_SIZE_KEY, String(percent));
}

export function loadTextBold(): boolean {
  return localStorage.getItem(TEXT_BOLD_KEY) === "1";
}

export function saveTextBold(bold: boolean): void {
  localStorage.setItem(TEXT_BOLD_KEY, bold ? "1" : "0");
}

/* Every option carries a real regular and bold and covers the whole modern Greek alphabet.
   Their line boxes and label widths were measured against the default before being offered,
   so none of them can overflow a row that fits today. */
export const FONT_FAMILIES = [
  { value: "noto-sans", label: "Noto Sans (προεπιλογή)", stack: '"Noto Sans Variable"' },
  { value: "fira-sans", label: "Fira Sans", stack: '"Fira Sans"' },
  { value: "roboto", label: "Roboto", stack: '"Roboto Variable"' },
  { value: "open-sans", label: "Open Sans", stack: '"Open Sans Variable"' }
] as const;

export type FontFamily = (typeof FONT_FAMILIES)[number]["value"];

export function loadFontFamily(): FontFamily {
  const stored = localStorage.getItem(FONT_FAMILY_KEY);
  return FONT_FAMILIES.some((font) => font.value === stored)
    ? (stored as FontFamily)
    : "noto-sans";
}

export function saveFontFamily(family: FontFamily): void {
  localStorage.setItem(FONT_FAMILY_KEY, family);
}

export function applyFontFamily(family: FontFamily): void {
  const match = FONT_FAMILIES.find((font) => font.value === family) ?? FONT_FAMILIES[0];
  document.documentElement.style.setProperty("--app-font", match.stack);
}

export function applyTextPrefs(percent: number, bold: boolean): void {
  document.documentElement.style.setProperty("--text-scale", String(percent / 100));
  document.documentElement.style.setProperty("--text-preference-weight", bold ? "800" : "550");
  document.documentElement.dataset.textBold = bold ? "1" : "0";
}

export type ProductScrollMode = "inner" | "page";

export function loadProductScrollMode(): ProductScrollMode {
  return localStorage.getItem(PRODUCT_SCROLL_KEY) === "page" ? "page" : "inner";
}

export function saveProductScrollMode(mode: ProductScrollMode): void {
  localStorage.setItem(PRODUCT_SCROLL_KEY, mode);
}

export type ProductDisplayMode = "list" | "tiles";

export function loadProductDisplayMode(): ProductDisplayMode {
  return localStorage.getItem(PRODUCT_DISPLAY_KEY) === "list" ? "list" : "tiles";
}

export function saveProductDisplayMode(mode: ProductDisplayMode): void {
  localStorage.setItem(PRODUCT_DISPLAY_KEY, mode);
}

export function loadProductImagesVisible(): boolean {
  return localStorage.getItem(PRODUCT_IMAGES_KEY) !== "0";
}

export function saveProductImagesVisible(visible: boolean): void {
  localStorage.setItem(PRODUCT_IMAGES_KEY, visible ? "1" : "0");
}

export type HistoryExpansionMode = "open" | "closed";

export function loadHistoryExpansionMode(): HistoryExpansionMode {
  return localStorage.getItem(HISTORY_EXPANSION_KEY) === "open" ? "open" : "closed";
}

export function saveHistoryExpansionMode(mode: HistoryExpansionMode): void {
  localStorage.setItem(HISTORY_EXPANSION_KEY, mode);
}

/* The chime on a new or changed order is separate from the browser notification: it plays even
   with the app in front, which is where it is most useful, and needs no permission. */
export function loadOrderSoundEnabled(): boolean {
  return localStorage.getItem(ORDER_SOUND_KEY) !== "0";
}

export function saveOrderSoundEnabled(on: boolean): void {
  localStorage.setItem(ORDER_SOUND_KEY, on ? "1" : "0");
}

/* Each waiter decides whether adding a product opens the Χωρίς/Extra card straight away or
   whether the line lands in the draft and the pencil is the only way in. */
export function loadDetailsPromptOnAdd(): boolean {
  return localStorage.getItem(DETAILS_PROMPT_KEY) !== "0";
}

export function saveDetailsPromptOnAdd(on: boolean): void {
  localStorage.setItem(DETAILS_PROMPT_KEY, on ? "1" : "0");
}

/* Unsent draft lines survive leaving the table by default; turning this off empties the draft
   the moment the table is closed so the next waiter starts clean. */
export function loadKeepUnsentDraft(): boolean {
  return localStorage.getItem(KEEP_UNSENT_DRAFT_KEY) !== "0";
}

export function saveKeepUnsentDraft(on: boolean): void {
  localStorage.setItem(KEEP_UNSENT_DRAFT_KEY, on ? "1" : "0");
}

export type CloseTableButtonStyle = "compact" | "full";

export function loadCloseTableButtonStyle(): CloseTableButtonStyle {
  return localStorage.getItem(CLOSE_BUTTON_STYLE_KEY) === "full" ? "full" : "compact";
}

export function saveCloseTableButtonStyle(style: CloseTableButtonStyle): void {
  localStorage.setItem(CLOSE_BUTTON_STYLE_KEY, style);
}

export type StickyKey =
  | "categories"
  | "actions"
  | "tabs"
  | "takeaway"
  | "comment";

export function loadSticky(key: StickyKey): boolean {
  return localStorage.getItem(STICKY_PREFIX + key) !== "0";
}

export function saveSticky(key: StickyKey, on: boolean): void {
  localStorage.setItem(STICKY_PREFIX + key, on ? "1" : "0");
}

/* Colour theme, separate from light/dark mode — same split as Android's KEY_THEME_COLOR and
   KEY_THEME_MODE. Order matches the app's dropdown. */
const THEME_COLOR_KEY = `${PREFIX}.themeColor`;

export const THEME_COLORS = [
  { value: "warm", label: "Warm Classic" },
  { value: "red", label: "Red Flame" },
  { value: "green", label: "Olive Green" },
  { value: "blue", label: "Aegean Blue" },
  { value: "teal", label: "Petrol Wave" },
  { value: "purple", label: "Plum Night" },
  { value: "mono", label: "Mono Ink" },
  { value: "gray", label: "Graphite Gray" },
  { value: "amber", label: "Amber Gold" }
] as const;

export type ThemeColor = (typeof THEME_COLORS)[number]["value"];

export function loadThemeColor(): ThemeColor {
  const stored = localStorage.getItem(THEME_COLOR_KEY);
  return (THEME_COLORS.find((entry) => entry.value === stored)?.value ?? "warm") as ThemeColor;
}

export function saveThemeColor(color: ThemeColor): void {
  localStorage.setItem(THEME_COLOR_KEY, color);
}

export function applyThemeColor(color: ThemeColor): void {
  document.documentElement.dataset.color = color;
}

/* ---------------------------------------------------------------------------------------------
   Preferences that follow the account rather than the browser.

   Written straight through localStorage rather than by editing every save function: one place to
   read and one to write means a setting added later syncs without anyone remembering to wire it
   up. The canonical names match the Android client's, which is what lets a change made on a
   phone show up here.
   --------------------------------------------------------------------------------------------- */

type PreferenceKind = "bool" | "int" | "text";

const SYNCED_PREFERENCES: { canonical: string; key: string; kind: PreferenceKind }[] = [
  { canonical: "ui_language", key: UI_LANGUAGE_KEY, kind: "text" },
  { canonical: "theme_mode", key: THEME_KEY, kind: "text" },
  { canonical: "theme_color", key: THEME_COLOR_KEY, kind: "text" },
  { canonical: "text_size_percent", key: TEXT_SIZE_KEY, kind: "int" },
  { canonical: "text_bold", key: TEXT_BOLD_KEY, kind: "bool" },
  { canonical: "font_family", key: FONT_FAMILY_KEY, kind: "text" },
  { canonical: "order_sound", key: ORDER_SOUND_KEY, kind: "bool" },
  { canonical: "product_display_mode", key: PRODUCT_DISPLAY_KEY, kind: "text" },
  { canonical: "product_images_visible", key: PRODUCT_IMAGES_KEY, kind: "bool" },
  { canonical: "product_scroll_mode", key: PRODUCT_SCROLL_KEY, kind: "text" },
  { canonical: "history_default_expanded", key: HISTORY_EXPANSION_KEY, kind: "text" },
  { canonical: "details_prompt_on_add", key: DETAILS_PROMPT_KEY, kind: "bool" },
  { canonical: "keep_unsent_draft", key: KEEP_UNSENT_DRAFT_KEY, kind: "bool" },
  { canonical: "table_view_mode", key: TABLE_VIEW_KEY, kind: "text" },
  { canonical: "table_sort_mode", key: TABLE_SORT_KEY, kind: "text" },
  { canonical: "table_employee_preview", key: TABLE_PREVIEW_KEY, kind: "bool" },
  { canonical: "table_tools_open", key: TABLE_TOOLS_OPEN_KEY, kind: "bool" },
  { canonical: "sticky_categories", key: `${STICKY_PREFIX}categories`, kind: "bool" },
  { canonical: "sticky_actions", key: `${STICKY_PREFIX}actions`, kind: "bool" },
  { canonical: "close_button_style", key: CLOSE_BUTTON_STYLE_KEY, kind: "text" },
  { canonical: "sticky_tabs", key: `${STICKY_PREFIX}tabs`, kind: "bool" },
  { canonical: "sticky_takeaway", key: `${STICKY_PREFIX}takeaway`, kind: "bool" },
  { canonical: "sticky_comment", key: `${STICKY_PREFIX}comment`, kind: "bool" }
];

/* The PWA stores booleans as "1"/"0" and the history mode as a word; Android stores real
   booleans. The canonical form is the typed one, so translate on the way out and back. */
export function collectSyncedPreferences(): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const entry of SYNCED_PREFERENCES) {
    const raw = localStorage.getItem(entry.key);
    if (raw === null) {
      continue;
    }
    if (entry.kind === "bool") {
      payload[entry.canonical] = raw !== "0";
    } else if (entry.kind === "int") {
      const value = Number(raw);
      if (Number.isFinite(value)) {
        payload[entry.canonical] = value;
      }
    } else if (entry.canonical === "history_default_expanded") {
      payload[entry.canonical] = raw === "open";
    } else {
      payload[entry.canonical] = raw;
    }
  }
  return payload;
}

export function applySyncedPreferences(remote: Record<string, unknown> | null | undefined): number {
  if (!remote) {
    return 0;
  }
  let applied = 0;
  for (const entry of SYNCED_PREFERENCES) {
    if (!(entry.canonical in remote)) {
      continue;
    }
    const value = remote[entry.canonical];
    if (entry.canonical === "ui_language") {
      if (value !== "el" && value !== "en") continue;
      setLanguage(value);
    } else if (entry.canonical === "history_default_expanded") {
      localStorage.setItem(entry.key, value ? "open" : "closed");
    } else if (entry.kind === "bool") {
      localStorage.setItem(entry.key, value ? "1" : "0");
    } else if (entry.kind === "int") {
      if (typeof value !== "number") {
        continue;
      }
      localStorage.setItem(entry.key, String(value));
    } else {
      if (typeof value !== "string" || !value) {
        continue;
      }
      localStorage.setItem(entry.key, value);
    }
    applied += 1;
  }
  return applied;
}

/* Watches the values rather than the writes.
   The first attempt wrapped localStorage.setItem, which silently does nothing: Storage is an
   exotic object where assigning a property stores an entry, so that only created an item keyed
   "setItem" and left the real method in place. Comparing the serialised preferences on a timer
   cannot fail that way, and it still catches settings added later with no call at each site. */
export function watchPreferenceChanges(onChange: () => void, intervalMs = 3_000): () => void {
  localStorage.removeItem("setItem"); // Litter from the wrapper described above.
  let previous = JSON.stringify(collectSyncedPreferences());
  const timer = window.setInterval(() => {
    const current = JSON.stringify(collectSyncedPreferences());
    if (current !== previous) {
      previous = current;
      onChange();
    }
  }, intervalMs);
  return () => window.clearInterval(timer);
}
